/*
  eslint-disable flights/no-use-effect-in-controller
  -- this line was auto generated, hence fix the issue timely
*/
import { t, useI18n } from "@bookingcom/lingojs-react";
import useSearchFiltersController from "../../useSearchFiltersController";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import useFormatPrice from "../../../../../hooks/useFormatPrice";
import flights_web_budget_layover_airports_filters from "../../../../../utils/experiments/funnel/flights_web_budget_layover_airports_filters";
import debounce from "lodash/debounce";

const useDebouncedCallback = <T extends (...args: any[]) => any>(callback: T, delay: number) => {
  const debouncedFn = useRef<ReturnType<typeof debounce>>();
  useEffect(() => {
    debouncedFn.current = debounce((...args: Parameters<T>) => {
      callback(...args);
    }, delay);
    return () => {
      debouncedFn.current?.cancel();
    };
  }, [callback, delay]);
  return useCallback((...args: Parameters<T>) => {
    debouncedFn.current?.(...args);
  }, []);
};

/**
 * Generates an array of exponentially increasing steps between the specified minimum and maximum values.
 * The function calculates the growth factor based on the number of desired steps and iteratively computes
 * each step by multiplying the minimum value by the growth factor raised to the power of the current step index.
 * Duplicate values are automatically removed to ensure a unique set of steps.
 */
function generateExponentialSteps(min: number, max: number, steps: number) {
  const result = new Set<number>();
  const factor = Math.pow(max / min, 1 / (steps - 1));
  for (let i = 0; i < steps; i++) {
    const value = Math.round(min * Math.pow(factor, i));
    result.add(value);
  }
  return Array.from(result);
}

export const useBudgetPerAdultFilterController = () => {
  const i18n = useI18n();
  const { formatPrice } = useFormatPrice();
  const allFilters = useSearchFiltersController();
  const { budgetPerAdult } = allFilters;
  const [stepValue, setStepValue] = useState(0);

  const debouncedBudgetSet = useDebouncedCallback(budgetPerAdult.set, 300);

  const handleOnChange = (index: number) => {
    flights_web_budget_layover_airports_filters.goals.interactedWithBudgetFilter();
    setStepValue(index);
  };

  const price = (units: number) => {
    return i18n.trans(
      t("flights_filters_price_per_adult", {
        variables: {
          max_price: formatPrice(
            { nanos: 0, units: Math.ceil(units + 1), currencyCode: budgetPerAdult.options.max.currencyCode },
            { decimalPlaces: 0 }
          )
        }
      })
    );
  };

  const isFilterAvailable =
    budgetPerAdult.options.max.units !== 0 &&
    budgetPerAdult.options.min.units !== 0 &&
    budgetPerAdult.options.min.units !== budgetPerAdult.options.max.units;

  const stepsValueArr = useMemo(() => {
    const min = budgetPerAdult.options.min.units;
    const max = budgetPerAdult.options.max.units;
    return generateExponentialSteps(min, max, 20);
  }, [budgetPerAdult.options.min.units, budgetPerAdult.options.max.units]);

  const getIndexByValue = useCallback(
    (value: number) => {
      const len = stepsValueArr.length;
      if (value <= stepsValueArr[0]) return 0;
      if (value >= stepsValueArr[len - 1]) return len - 1;
      let left = 0;
      let right = len - 1;
      while (left < right) {
        const mid = Math.floor((left + right) / 2);
        const valueMid = stepsValueArr[mid];
        if (valueMid === value || (valueMid > value && stepsValueArr[mid - 1] < value)) return mid;
        else if (valueMid < value) left = mid + 1;
        else right = mid;
      }
      return left;
    },
    [stepsValueArr]
  );

  useEffect(() => {
    setStepValue(getIndexByValue(budgetPerAdult.value));
  }, [getIndexByValue, budgetPerAdult.value, stepsValueArr]);

  const handleChangeCommit = useCallback(
    (index: number) => {
      debouncedBudgetSet(stepsValueArr[index]);
    },
    [debouncedBudgetSet, stepsValueArr]
  );

  return {
    isFilterAvailable,
    title: i18n.trans(t("flights_filter_price_title")),
    resetLabel: i18n.trans(t("flights_filter_reset")),
    showReset: budgetPerAdult.touched,
    onClickReset: budgetPerAdult.reset,
    min: 0,
    max: stepsValueArr.length - 1,
    value: stepValue,
    ariaLabel: i18n.trans(t("a11y_flights_filter_price_announce")),
    label: price(budgetPerAdult.value),
    onChange: (index: number) => handleOnChange(index),
    onChangeCommit: handleChangeCommit,
    renderValue: () => null,
    interval: 1,
    renderTooltipValue: (index: number) => price(stepsValueArr[index])
  };
};
